import { SWRFetcher } from '@commenda/utils';
import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  IIndirectTaxJurisdictionFilterSet,
  IIndirectTaxRegistrationJurisdictionsParams,
  IIndirectTaxRegistrationJurisdictionsResponse,
} from './types';

function buildQuery(
  params: IIndirectTaxRegistrationJurisdictionsParams
): string {
  const query = new URLSearchParams();
  query.append('corporationId', params.corporationId.toString());

  if (params.countries?.length) {
    params.countries.forEach((c) => query.append('countries', c));
  }
  if (params.states?.length) {
    params.states.forEach((s) => query.append('states', s));
  }
  if (params.types?.length) {
    params.types.forEach((t) => query.append('types', t));
  }
  if (params.search) {
    query.append('search', params.search);
  }
  if (params.limit) {
    query.append('limit', params.limit.toString());
  }
  if (params.cursor) {
    query.append('cursor', params.cursor);
  }

  return query.toString();
}

export function useIndirectTaxRegistrationJurisdictions(
  params: IIndirectTaxRegistrationJurisdictionsParams | null
) {
  const { data, mutate, error, isLoading } =
    useSWR<IIndirectTaxRegistrationJurisdictionsResponse>(
      params
        ? `new-sales-tax/registrations-v2/content/jurisdictions?${buildQuery(params)}`
        : null,
      SWRFetcher({ method: 'GET' }, undefined)
    );

  return {
    data,
    mutate,
    error,
    isLoading,
  };
}

// The most the backend serves in one page.
const PAGE_LIMIT = 100;

/** Every jurisdiction one filter set matches, read page by page. */
export function useAllIndirectTaxRegistrationJurisdictions(
  corporationId: number,
  filterSet: IIndirectTaxJurisdictionFilterSet | null
) {
  const getKey = (
    _pageIndex: number,
    previousPage: IIndirectTaxRegistrationJurisdictionsResponse | null
  ): string | null => {
    if (!filterSet) return null;
    if (previousPage && !previousPage.cursor) return null;
    const query = buildQuery({
      corporationId,
      countries: filterSet.countries,
      types: filterSet.types,
      limit: PAGE_LIMIT,
      cursor: previousPage?.cursor,
    });
    return `new-sales-tax/registrations-v2/content/jurisdictions?${query}`;
  };

  const { data, error, isValidating, setSize, mutate } =
    useSWRInfinite<IIndirectTaxRegistrationJurisdictionsResponse>(
      getKey,
      SWRFetcher({ method: 'GET' }, undefined),
      { revalidateFirstPage: false }
    );

  const hasMore = Boolean(filterSet && data?.[data.length - 1]?.cursor);
  const jurisdictions = useMemo(
    () =>
      filterSet ? (data?.flatMap((page) => page.jurisdictions) ?? []) : [],
    [data, filterSet]
  );

  useEffect(() => {
    if (hasMore && !isValidating && !error) setSize((size) => size + 1);
  }, [hasMore, isValidating, error, setSize]);

  return {
    jurisdictions,
    isLoading: Boolean(filterSet) && !error && (!data || hasMore),
    error: filterSet ? error : undefined,
    mutate,
  };
}
